/**
 * Client-side utility for authenticated asset access
 * This provides methods to create blob URLs for authenticated assets
 * Compatible with static deployments like Cloudflare Pages
 */
import { apiClient } from './client';


// Cache for blob URLs to avoid creating duplicates
const blobUrlCache = new Map<string, string>();

/**
 * Creates a blob URL for an asset that requires authentication
 * @param assetId The UUID of the asset to access
 * @param bypassCache If true, forces a fresh fetch even if cached
 * @returns A Promise that resolves to a blob URL that can be used in img tags
 */
export const createAuthenticatedBlobUrl = async (
  assetId: string,
  bypassCache: boolean = false
): Promise<string> => {
  // Check if we already have a blob URL for this asset (unless bypassing cache)
  if (!bypassCache && blobUrlCache.has(assetId)) {
    return blobUrlCache.get(assetId)!;
  }

  try {
    // Add cache-busting timestamp to force fresh content
    const cacheBuster = bypassCache ? `?t=${Date.now()}` : '';

    // Make an authenticated request using our API client
    const response = await apiClient.get(
      `/api/assets/${encodeURIComponent(assetId)}/content${cacheBuster}`,
      {
        responseType: 'blob'
      }
    );

    // Create a blob URL from the response data
    const blob = new Blob([response.data], {
      type: response.headers['content-type'] || 'application/octet-stream'
    });

    const blobUrl = URL.createObjectURL(blob);

    // Cache the blob URL
    blobUrlCache.set(assetId, blobUrl);

    return blobUrl;
  } catch (error) {
    console.error('Error creating blob URL for asset:', error);
    throw error;
  }
};

/**
 * Revokes all cached blob URLs to prevent memory leaks
 * Call this when the component unmounts
 */
export const revokeBlobUrls = (): void => {
  blobUrlCache.forEach((url) => {
    try {
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error revoking blob URL:', error);
    }
  });
  blobUrlCache.clear();
};

/**
 * Revokes a specific blob URL from the cache
 * @param assetId The asset ID whose blob URL should be revoked
 */
export const revokeBlobUrl = (assetId: string): void => {
  const url = blobUrlCache.get(assetId);
  if (url) {
    try {
      URL.revokeObjectURL(url);
      blobUrlCache.delete(assetId);
    } catch (error) {
      console.error(`Error revoking blob URL for asset ${assetId}:`, error);
    }
  }
};

/**
 * Creates a direct URL with auth token as a query parameter
 * This is an alternative approach that doesn't use blob URLs
 * @param assetId The UUID of the asset
 * @returns Promise with a URL containing the auth token
 */
export const createTokenizedUrl = async (assetId: string): Promise<string> => {
  try {
    // Get token from our auth system
    const token = localStorage.getItem('authToken');
    if (token) {
      const baseUrl = apiClient.defaults.baseURL || '';
      return `${baseUrl}/api/assets/${encodeURIComponent(assetId)}/content?token=${encodeURIComponent(token)}`;
    } else {
      // Don't make the request if we're not authenticated
      console.error('[API Client] Not authenticated, rejecting request');
      return Promise.reject(new Error('Not authenticated'));
    }
  } catch (error) {
    console.error('Error creating tokenized URL:', error);
    throw error;
  }
};
